import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaBook, FaGlobeEurope, FaMusic } from 'react-icons/fa';
import Avatar from '../Common/Avatar';
import TeacherContacts from './TeacherContacts';
import { useTranslation } from '../../utils/translations';
import {
  WEEK_DAYS,
  dayKey,
  dayShortKey,
  nextStopFrom,
  normalizeDay,
  peopleContacts,
  todayName,
} from './familyCompat';
import './DateGroupedView.css';

/* Week calendar, Monday–Saturday: a row of day columns you swipe sideways.
   Hierarchy comes from spacing and type, not boxes — one rule under the day
   names, cards as soft white tiles on the page, colour reserved for the
   activity name and for today. Nothing about the layout moves while you
   scroll: the row is as tall as its tallest day and stays that way.

   A wide screen fits all six days; days with no classes shrink to a short
   label so the days that matter get the room. A phone shows ONLY the days
   with classes, back to back: each rests centred with a sliver of the next
   class day on either side, its cards dimmed so the resting day is the focus
   — the cue that there is more — and tapping a sliver brings that day in. The first and last sit against their edge. An
   empty day on a phone would be a blank screen with nothing to swipe towards,
   so it is simply not there, today included. The row opens on today when
   today has classes, otherwise on the next day that does. */

/* A column by the day it shows. Looked up by attribute, never by position:
   on a phone only the class days render, so a weekday's index into the
   rendered columns is meaningless (Wednesday + Friday alone was dimming
   everything and landing nowhere). */
const columnFor = (scroller, day) => scroller.querySelector(`[data-day="${day}"]`);

/* Phone layout: one day at a time. Mirrors the stylesheet's breakpoint. */
const PHONE_QUERY = '(max-width: 539px)';
const usePhone = () => {
  const [phone, setPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(PHONE_QUERY);
    const onChange = (e) => setPhone(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return phone;
};

const TYPE_META = {
  sundaySchool: { icon: FaBook, cls: 'ss' },
  russianSchool: { icon: FaGlobeEurope, cls: 'rs' },
  childrensChoir: { icon: FaMusic, cls: 'ch' },
};

const WeekBlock = ({ block, t }) => {
  const { icon: Icon, cls } = TYPE_META[block.type];
  const isChoir = block.type === 'childrensChoir';

  return (
    <article className={`week-block week-${cls}`}>
      {/* who and what on the left; class and room set right, so the card's
          top half uses its full width instead of listing to the left */}
      <div className="week-block-head">
        <div className="week-block-who">
          <Avatar name={block.student.name} gender={block.student.gender} />
          <div className="week-block-title">
            <h4 className="week-block-name">{block.student.name}</h4>
            <p className="week-block-activity">
              <Icon aria-hidden="true" />
              {t(`activities.${block.type}`)}
            </p>
          </div>
        </div>
        <dl className="week-block-facts">
          <div className="week-fact">
            <dt>{isChoir ? t('details.choirNumber') : t('details.class')}</dt>
            <dd>{block.main}</dd>
          </div>
          {block.room && (
            <div className="week-fact">
              <dt>{t('details.room')}</dt>
              <dd>{block.room}</dd>
            </div>
          )}
        </dl>
      </div>

      {block.teachers.length > 0 && (
        <div className="week-block-people">
          <span className="week-people-label">
            {isChoir ? t('details.choirLeaders') : t('details.teachers')}
          </span>
          <div className="week-people-names">
            <TeacherContacts people={block.teachers} />
          </div>
        </div>
      )}
    </article>
  );
};

WeekBlock.propTypes = {
  block: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
};

const DateGroupedView = ({ students }) => {
  const { t } = useTranslation();
  const today = todayName();
  const phone = usePhone();
  const scrollerRef = useRef(null);

  const blocksByDay = useMemo(() => {
    const map = Object.fromEntries(WEEK_DAYS.map((d) => [d, []]));
    students.forEach((student) => {
      student.activities.forEach((sa) => {
        const d = sa?.details;
        // Day strings arrive in Russian (v2) or English (v1), any casing;
        // unknown/missing days simply omit the block from the calendar.
        const day = normalizeDay(d?.day);
        if (!day || !map[day]) return;
        map[day].push({
          key: `${student.id}-${sa.id}`,
          type: sa.id,
          student,
          main: sa.id === 'childrensChoir' ? d.choirNumber : d.class,
          room: d.classroom || d.choirRoom,
          teachers: peopleContacts(d),
        });
      });
    });
    return map;
  }, [students]);

  /* "Stops" are the columns the row can rest on. Desktop: every day with
     classes, plus today even when empty (a parent expects to find it, and there
     is room). Phone: only days with classes — an empty column there is a blank
     screen. The other days shrink to rails on desktop and do not render at all
     on a phone. */
  const stops = useMemo(
    () => WEEK_DAYS.filter((d) => blocksByDay[d].length > 0 || (!phone && d === today)),
    [blocksByDay, today, phone]
  );
  const columns = phone ? stops : WEEK_DAYS;

  /* Where the row is resting: the stop whose centre is nearest the middle of
     the viewport. Used both for the counts and to know which sliver a tap
     landed on. */
  const currentStop = useCallback(
    (el) => {
      const mid = el.scrollLeft + el.clientWidth / 2;
      let best = null;
      let bestDist = Infinity;
      stops.forEach((day) => {
        const col = columnFor(el, day);
        if (!col) return;
        const dist = Math.abs(col.offsetLeft + col.offsetWidth / 2 - mid);
        if (dist < bestDist) {
          best = day;
          bestDist = dist;
        }
      });
      return best;
    },
    [stops]
  );

  const scrollToStop = useCallback(
    (el, day, { instant = false } = {}) => {
      const col = columnFor(el, day);
      if (!col) return;
      const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
      const first = stops[0];
      const last = stops[stops.length - 1];
      // first/last rest against their edge; everything else rests centred —
      // the browser clamps, so this also matches what the snap points do
      let left;
      if (day === first) left = col.offsetLeft - pad;
      else if (day === last) left = col.offsetLeft + col.offsetWidth + pad - el.clientWidth;
      else left = col.offsetLeft + col.offsetWidth / 2 - el.clientWidth / 2;
      left = Math.max(0, Math.min(left, el.scrollWidth - el.clientWidth));
      if (instant) {
        // the stylesheet animates programmatic scrolls; the opening position
        // should simply be there, not slide in from Monday
        const prev = el.style.scrollBehavior;
        el.style.scrollBehavior = 'auto';
        el.scrollLeft = left;
        el.style.scrollBehavior = prev;
      } else {
        el.scrollLeft = left;
      }
    },
    [stops]
  );

  /* Which day the row is resting on. The stylesheet dims the cards of every
     other day on a phone so the resting day is the focus; the day labels stay
     at full strength - it is a calendar. Read-only measurement. */
  const [current, setCurrent] = useState(null);
  const [scrolls, setScrolls] = useState(false);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const overflows = el.scrollWidth > el.clientWidth + 4;
    setScrolls(overflows);
    setCurrent(overflows ? currentStop(el) : null);
  }, [currentStop]);

  // Open on today when today has classes, else on the next day that does.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const landing = nextStopFrom(today, stops.filter((d) => blocksByDay[d].length > 0));
    if (landing && el.scrollWidth > el.clientWidth + 4) {
      scrollToStop(el, landing, { instant: true });
    }
    measure();
  }, [blocksByDay, stops, today, scrollToStop, measure]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  // Tapping a sliver brings that day in. Taps on the resting day (and on any
  // control inside a card) are left alone.
  const onColumnClick = (e, day) => {
    const el = scrollerRef.current;
    if (!el || e.target.closest('button, a')) return;
    if (!stops.includes(day) || currentStop(el) === day) return;
    scrollToStop(el, day);
    // the glide fires scroll events as it goes; read once now and once at the
    // settled state, so the resting day is right even where a programmatic
    // scroll fires no scroll events at all
    measure();
    window.setTimeout(measure, 450);
  };

  return (
    <div className={`week-cal-wrap${scrolls ? ' week-cal-scrolls' : ''}`}>
      {columns.length === 0 && <p className="week-none">{t('main.noClassesThisWeek')}</p>}
      <div className="week-cal" ref={scrollerRef} onScroll={measure}>
        {columns.map((day) => {
          const blocks = blocksByDay[day];
          const isToday = day === today;
          const rail = !stops.includes(day);
          const label = t(dayKey(day));
          const cls = [
            'week-col',
            rail && 'week-col-rail',
            isToday && 'week-col-today',
            day === current && 'week-col-current',
            day === stops[0] && 'week-col-first',
            day === stops[stops.length - 1] && 'week-col-last',
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <section
              key={day}
              data-day={day}
              className={cls}
              aria-label={blocks.length === 0 ? `${label}: ${t('main.noClasses')}` : label}
              title={rail ? `${label}: ${t('main.noClasses')}` : undefined}
              onClick={(e) => onColumnClick(e, day)}
            >
              <header className="week-col-head">
                <span className="week-col-day">{rail ? t(dayShortKey(day)) : label}</span>
                {isToday && <span className="week-today">{t('main.today')}</span>}
              </header>
              <div className="week-col-body">
                {blocks.length === 0
                  ? !rail && <p className="week-empty">{t('main.noClasses')}</p>
                  : blocks.map((b) => <WeekBlock key={b.key} block={b} t={t} />)}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

DateGroupedView.propTypes = {
  students: PropTypes.array.isRequired,
  // kept for call-site compatibility; day/type meta comes from the students
  activities: PropTypes.array,
};

export default DateGroupedView;
